import React, {forwardRef, ReactNode, useId} from 'react';
import Button from '@mui/material/Button';
import {Typography} from '@mui/material';
import AurLoadingIndicator from '../aur-loading-indicator/AurLoadingIndictor';

interface IButtonProps {
  label?: string | undefined;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  type: 'text' | 'exit' | 'continue' | 'secondary' | 'submit';
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size?: 'Large' | 'Medium' | 'Small';
  classNames?: string;
  title?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  ariaDescribedBy?: string;
  ariaDescription?: string;
  dataTestId?: string;
  name?: string;
  buttonType?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
  minWidth?: number;
  border?: string;
  ariaLabel?: string;
  showLoader?: boolean;
  ariaControls?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  loaderClass?: string;
  showLabelWithLoader?: boolean;
  disableHint?: string;
  keepDisabledFocusable?: boolean; // Controls whether a disabled button remains keyboard focusable.
  id?: string;
}

/*
Component for button 
*/
export const AurButton = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      label,
      type,
      disabled = false,
      onClick,
      startIcon,
      endIcon,
      size = 'Large',
      classNames = '',
      title = '',
      autoFocus = false,
      tabIndex,
      ariaDescribedBy,
      ariaDescription = '',
      dataTestId,
      name = '',
      buttonType = 'button',
      children,
      minWidth,
      border,
      ariaLabel,
      showLoader = false,
      ariaControls,
      onKeyDown,
      loaderClass,
      showLabelWithLoader,
      disableHint,
      keepDisabledFocusable = false,
      id,
    },
    ref
  ) => {
    const isNativeDisabled = disabled && !keepDisabledFocusable;
    const renderContent = () => {
      if (showLabelWithLoader) {
        return (
          <>
            {showLoader && (
              <AurLoadingIndicator
                size={24}
                className={`progress-icon ${loaderClass ?? ''}`}
              />
            )}
            {label && (
              <Typography
                className={`typo-text button${size}_size button-text`}
              >
                {label}
              </Typography>
            )}
          </>
        );
      }
      return (
        <>
          {showLoader ? (
            <AurLoadingIndicator
              size={24}
              className={`progress-icon ${loaderClass ?? ''}`}
            />
          ) : (
            label && (
              <Typography
                className={`typo-text button${size}_size button-text`}
              >
                {label}
              </Typography>
            )
          )}
        </>
      );
    };
    const variant =
      type === 'exit' ? 'outlined' : type === 'text' ? 'text' : 'contained';
    const hintId = useId();
    const description = disabled ? disableHint : ariaDescription;
    const describedBy = ariaDescribedBy ?? (description ? hintId : undefined);
    let disabledClassName = '';

    if (disabled) {
      disabledClassName = keepDisabledFocusable
        ? 'button-focusable-disabled'
        : 'button-disabled';
    }
    return (
      <>
        <Button
          ref={ref}
          className={`button-${type} button${size}_Size  button-icon ${disabledClassName} ${classNames}`}
          variant={variant}
          disabled={isNativeDisabled}
          aria-disabled={keepDisabledFocusable ? disabled : undefined}
          disableRipple={keepDisabledFocusable && disabled}
          onClick={(e) => {
            if (keepDisabledFocusable && disabled) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            onClick?.(e);
          }}
          id={id}
          title={title}
          tabIndex={tabIndex}
          autoFocus={autoFocus}
          startIcon={startIcon}
          aria-label={ariaLabel || label}
          aria-describedby={describedBy}
          data-testid={dataTestId}
          endIcon={endIcon}
          focusVisibleClassName={name}
          type={buttonType}
          aria-controls={ariaControls}
          onKeyDown={(e) => {
            if (
              keepDisabledFocusable &&
              disabled &&
              (e.key === 'Enter' || e.key === ' ')
            ) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            onKeyDown?.(e);
          }}
          name={name}
          style={{
            minWidth: `${minWidth && minWidth}px`,
            border: `${border && 'none'}`,
          }}
          sx={{
            '& .MuiButton-startIcon': {
              marginRight: '0px', // Adjust spacing
              marginLeft: '0px',
              // Add any other styles here
            },
            '& .MuiButton-startIcon svg': {
              opacity: disabled ? 0.5 : 1,
              filter: disabled ? 'grayscale(100%)' : 'none',
            },
          }}
        >
          {renderContent()}
          {children}
        </Button>
        {description && (
          <span id={hintId} className="sr-only">
            {description}
          </span>
        )}
      </>
    );
  }
);

export default AurButton;
